import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
  AccessibleGridCell,
  FullyAccessibleGrid,
  GridCellSummary,
  GridType,
  GridWithMinimalCells,
  isAccessibleGridCell,
  isFullyAccessibleGrid,
} from 'constants/SampleConstants';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {GRID_VIEW_RENDER_FINISHED_EVENT} from 'performance/processInventoryEvents';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {getSortedSelectedCells} from 'samples/grids/GridUtil';
import GridVisualization from 'samples/grids/GridVisualization';
import useGridContentLots from 'samples/grids/hooks/useGridContentLots';
import {buildDraftGridWithScannedContainers} from 'samples/grids/ScanToMoveGridUtil';
import {setScanningCoordinates, setSelectedCells} from 'samples/GridUIReduxActions';
import {PlateMapPreferences} from 'samples/plate-maps/PlateMapConstants';
import {
  formatGridCoordinates,
  hasNewPlateAnnotationFillFeatures,
  shouldShowNewFeatureBanner,
} from 'samples/SampleUtil';
import StorableBanner from 'samples/storables/StorableBanner';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import usePreference from 'ui-library/hooks/usePreference';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';

import GridCellDetails from './GridCellDetails';
import GridCellsSelector, {isCellEmpty, isCellOccupiedAndAccessible} from './GridCellsSelector';
import {GridTabChildProps} from './GridContainer';

export interface GridViewProps extends GridTabChildProps {}

// Stable fallback so the selector below doesn't return a new array reference on every call.
const EMPTY_SELECTED_CELLS: Array<AccessibleGridCell> = [];

// Contents of the plate/box tab for grid entities
export default function GridView(props: GridViewProps): JSX.Element {
  useGridContentLots(props.grid);
  const dispatch = useDispatch<BenchlingDispatch>();
  const selectedCells = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[props.grid.id]?.selectedCells ?? EMPTY_SELECTED_CELLS
  );
  const isScanMode = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[props.grid.id]?.isScanMode ?? false
  );
  const scanModeCells = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[props.grid.id]?.scanModeCells ?? null
  );
  const scanningCoordinates = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[props.grid.id]?.scanningCoordinates
  );
  const [hideNewFeatureBanner, setHideNewFeatureBanner] = usePreference<boolean>(
    PlateMapPreferences.HAS_DISMISSED_TOO_BIG_PLATE_BANNER
  );
  const [selectedScanCells, setSelectedScanCells] = useState<Array<GridCellSummary>>([]);
  const [draftScanningGrid, setDraftScanningGrid] = useState<GridWithMinimalCells>(props.grid);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  // If not all containers in the plate are accessible, don't show the new grid visualization. This constraint
  // has no impact for well plates (all wells will be accessible), but friendly handling for inaccessible
  // containers will be needed in order to support the new viz for matrix plates.
  const showNewGridVisualization =
    hasNewPlateAnnotationFillFeatures(props.grid) && isFullyAccessibleGrid(props.grid);

  function handleSelectCells(selectedCells: Array<AccessibleGridCell>): void {
    dispatch(setSelectedCells(props.grid.id, selectedCells));
  }

  const {grid} = props;

  // Update the selected scan mode cell to match the scanning coordinates when they change
  // Currently looked up from the mapping of scanModeCells, but that does make that a dependency. Once it's
  // possible for those to change we should re-assess if parseGridPosition would be a better option. That
  // would need additional layers of error handling though so that we don't crash when users manually enter
  // an invalid set of coordinates
  useEffect(() => {
    if (scanningCoordinates != null && scanModeCells != null) {
      const selected = scanModeCells[scanningCoordinates] ? [scanModeCells[scanningCoordinates]] : [];
      setSelectedScanCells(selected);
    }
  }, [scanningCoordinates, scanModeCells]);

  // Update the draft scanning grid with the scanned containers. This is used to render GridCellsSelector
  // with the staged containers when in scan mode
  useEffect(() => {
    if (scanModeCells != null) {
      setDraftScanningGrid(buildDraftGridWithScannedContainers(grid, scanModeCells));
    }
  }, [scanModeCells, grid]);

  // Selecting a cell in scan mode == setting the scanning coordinates to that cell
  function handleSelectEmptyCellForScan(cells: Array<GridCellSummary>): void {
    if (cells.length < 1) {
      return;
    }
    dispatch(
      setScanningCoordinates(props.grid.id, formatGridCoordinates(cells[0].rowIndex, cells[0].columnIndex))
    );
  }

  const gridType = APIUtil.apiIdToType(props.grid.id) as unknown as GridType;
  const sortedSelectedCells = getSortedSelectedCells(props.grid, selectedCells);
  const nonNullSelectedCellSummaries: Array<GridCellSummary> = sortedSelectedCells
    .filter((cell): cell is AccessibleGridCell => isAccessibleGridCell(cell))
    .map((cell) => pick(cell, 'columnIndex', 'rowIndex'));

  return (
    <div {...getTestProps({component: 'GridView', element: 'root'})}>
      <StorableBanner isWritable={props.grid.permissions.writable} storable={props.grid} />
      <div className="react-tab-container tab-fixed-vertical-scroll" ref={backgroundRef}>
        <div
          className={css(showNewGridVisualization ? styles.plateMapContainer : styles.container)}
          ref={containerRef}
        >
          <h1 className={css(styles.gridName)} ref={titleRef}>
            {props.grid.name}
          </h1>
          {shouldShowNewFeatureBanner(props.grid) && !hideNewFeatureBanner && (
            <div className={css(styles.newFeatureBanner)}>
              <InlineBanner
                bannerType={InlineBannerType.INFO}
                onDismiss={() => {
                  setHideNewFeatureBanner(true);
                }}
                text="The updated plate annotation and fill tools only support plates up to 24 wells by 16 wells"
              />
            </div>
          )}
          <BenchlingProfiler
            {...GRID_VIEW_RENDER_FINISHED_EVENT}
            extra={{
              numTotalCells: props.grid.storableSchema.width * props.grid.storableSchema.height,
              numSelectedCells: sortedSelectedCells.length,
            }}
          >
            {showNewGridVisualization ? (
              <GridVisualization
                grid={props.grid as FullyAccessibleGrid}
                onSelectCells={handleSelectCells}
                selectedCells={selectedCells}
                deselectWellsOnClickRefs={[backgroundRef, containerRef, titleRef]}
              />
            ) : isScanMode ? (
              <GridCellsSelector
                grid={draftScanningGrid}
                isCompact={false}
                onSelectCells={handleSelectEmptyCellForScan}
                canSelectMultiple={false}
                selectedCells={selectedScanCells}
                checkCell={(grid, cell) => isCellEmpty(grid, cell)}
                invalidCellMessage="Cannot scan into filled position"
              />
            ) : (
              <GridCellsSelector
                // TODO(BNCH-93885) Return a different message when trying to select an inaccessible container
                // TODO(BNCH-93885) Add a tooltip on the inaccessible cells so it's more obvious why they can't be selected
                checkCell={(grid, cell) => isCellOccupiedAndAccessible(grid, cell)}
                grid={props.grid}
                isCompact={false}
                onSelectCells={handleSelectCells}
                selectedCells={nonNullSelectedCellSummaries}
              />
            )}
          </BenchlingProfiler>
          {(() => {
            if (props.grid.isLoading) {
              return (
                <div className="u-padding-top">
                  <div className="text-center u-spacing-top">
                    <HorizontalThrobber />
                  </div>
                </div>
              );
            } else if (sortedSelectedCells.length > 0) {
              return (
                <GridCellDetails
                  cells={sortedSelectedCells}
                  gridType={gridType}
                  grid={props.grid}
                  isWritable={props.grid.permissions.writable}
                />
              );
            }
            return null;
          })()}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    margin: 'auto',
    maxWidth: 1000,
  },
  plateMapContainer: {
    maxWidth: 1000,
  },
  gridName: {
    marginBottom: 16,
  },
  newFeatureBanner: {
    marginBottom: 16,
  },
});
